import AppContainer from '@components/common/container/AppContainer';
import styled from '@emotion/styled';
import { useRef } from 'react';
import useAdminProducts from '@hooks/admin/useAdminProducts';
import { useNavigate, useParams } from 'react-router-dom';
import { RiArrowRightLine } from '@remixicon/react';
import CategoryDragAndDropContent from '@components/admin/product-category/CategoryDragAndDropContent';
import { colFlex, rowFlex } from '@styles/flexStyles';
import { Color } from '@resources/colors';
import NewCommonButton from '@components/common/button/NewCommonButton';
import OnboardingStepHint from '@components/admin/workspace/onboarding/OnboardingStepHint';
import { ONBOARDING_STEP } from '@components/admin/workspace/onboarding/onboardingData';
import { getAdminProductsPath } from '@constants/routes';

const Container = styled.div`
  width: 680px;
  gap: 15px;
  padding-top: 24px;
  ${colFlex({ align: 'center' })}
`;

const CategoriesInputContainer = styled.div`
  width: 100%;
  padding-bottom: 32px;
  ${colFlex({ justify: 'center' })}
`;

const CategoriesItemContainer = styled.div`
  width: 100%;
  ${colFlex({ justify: 'center' })}
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #464a4d;
  padding-bottom: 15px;
`;

const InputRow = styled.div`
  width: 100%;
  gap: 12px;
  ${rowFlex({ justify: 'space-between', align: 'center' })}
`;

const CategoryInput = styled.input`
  flex: 1;
  border: none;
  border-bottom: 1px solid #e8eef2;
  padding: 12px 4px;
  font-size: 16px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-bottom-color: ${Color.KIO_ORANGE};
    caret-color: ${Color.KIO_ORANGE};
  }

  &::placeholder {
    color: #e8eef2;
  }
`;

const ProductLinkButton = styled(NewCommonButton)`
  width: auto;
  padding: 0 14px 0 18px;
`;

function AdminProductCategories() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { addCategory } = useAdminProducts(workspaceId);
  const categoryInputRef = useRef<HTMLInputElement>(null);

  const addCategoryHandler = () => {
    const userInput = categoryInputRef.current?.value;

    if (!userInput) {
      alert('카테고리를 입력해주세요');
      return;
    }

    addCategory(userInput);
    if (categoryInputRef.current) categoryInputRef.current.value = '';
  };

  const handleGoToProducts = () => {
    if (!workspaceId) return;

    navigate(getAdminProductsPath(workspaceId));
  };

  return (
    <AppContainer useFlex={colFlex({ justify: 'start', align: 'center' })}>
      <Container className={'admin-product-categories-container'}>
        <OnboardingStepHint
          step={ONBOARDING_STEP.MENU}
          action={
            <ProductLinkButton
              type="button"
              size="xs"
              color="blue_gray"
              customColors={{ color: Color.KIO_ORANGE, border: `1px solid ${Color.KIO_ORANGE}`, hoverBackground: Color.KIO_ORANGE_FAINT }}
              onClick={handleGoToProducts}
            >
              상품 추가하러 가기
              <RiArrowRightLine size={14} />
            </ProductLinkButton>
          }
        />
        <CategoriesInputContainer className={'categories-input-container'}>
          <SectionTitle>등록할 카테고리명</SectionTitle>
          <InputRow>
            <CategoryInput ref={categoryInputRef} type="text" placeholder="카테고리 이름을 입력해주세요" />
            <NewCommonButton onClick={addCategoryHandler} size="sm">
              등록
            </NewCommonButton>
          </InputRow>
        </CategoriesInputContainer>

        <CategoriesItemContainer className={'categories-item-container'}>
          <SectionTitle>카테고리 순서</SectionTitle>
          <CategoryDragAndDropContent />
        </CategoriesItemContainer>
      </Container>
    </AppContainer>
  );
}

export default AdminProductCategories;
